import { useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getErrorMessage } from 'shared/utils/errorMessage';
import { useDebounce } from 'shared/hooks/useDebounce';
import { getBukuTarifLogList } from 'features/admin-logs/api/get-buku-tarif-log-list';
import {
  adminLogKeys,
  adminLogStaleTime,
} from 'features/admin-logs/api/queryKeys';

const DEFAULT_SORT = '-starting_date';
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

export const useBukuTarifLogList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const searchRaw = searchParams.get('search') ?? '';
  const sort = searchParams.get('sort') ?? DEFAULT_SORT;
  const page = Number(searchParams.get('page')) || DEFAULT_PAGE;
  const pageSize = Number(searchParams.get('pageSize')) || DEFAULT_PAGE_SIZE;

  const search = useDebounce(searchRaw, 500);

  // Init URL params if missing
  useEffect(() => {
    const hasPage = searchParams.has('page');
    const hasPageSize = searchParams.has('pageSize');

    if (!hasPage || !hasPageSize) {
      setSearchParams(
        (prev) => {
          if (!hasPage) prev.set('page', String(DEFAULT_PAGE));
          if (!hasPageSize) prev.set('pageSize', String(DEFAULT_PAGE_SIZE));
          return prev;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  const { data, isFetching, isLoading } = useQuery(
    adminLogKeys.bukuTarifList({ page, pageSize, search, sort }),
    () => getBukuTarifLogList({ search, page, sort, limit: pageSize }),
    {
      staleTime: adminLogStaleTime.LIST,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onError: (error) => {
        const message = getErrorMessage(error);
        toast.error(`List buku tarif log: ${message}`);
      },
    }
  );

  const updateSearchParams = useCallback(
    (params) => {
      setSearchParams(
        (prev) => {
          Object.entries(params).forEach(([key, value]) => {
            if (value === null || value === undefined) {
              prev.delete(key);
            } else {
              prev.set(key, String(value));
            }
          });
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize) => {
      updateSearchParams({ pageSize: newPageSize, page: 1 });
    },
    [updateSearchParams]
  );

  const handlePaginationChange = useCallback(
    (newPage, newPageSize) => {
      updateSearchParams({ page: newPage, pageSize: newPageSize });
    },
    [updateSearchParams]
  );

  const handleSortOrderChange = useCallback(
    (newSort) => {
      updateSearchParams({ sort: newSort });
    },
    [updateSearchParams]
  );

  const handleSearchChange = useCallback(
    (e) => {
      const value = e?.target?.value ?? e;
      updateSearchParams({ search: value, page: 1 });
    },
    [updateSearchParams]
  );

  return {
    data,
    isFetching,
    isLoading,
    page,
    pageSize,
    searchRaw,
    sort,
    navigate,
    handlePageSizeChange,
    handlePaginationChange,
    handleSortOrderChange,
    handleSearchChange,
  };
};
